import { fetchNonce, loginWithSiwe } from '@/api/auth';
import { useFeatureFlags } from '@/configs';
import { KEY_AUTH, useAuth } from '@/store/auth';
import { CONSTANT } from '@/store/constants';
import { ErrorMessage } from '@/utils/errors';
import { clearTokens, saveTokens } from '@/utils/token-storage';
import { MiniKit } from '@worldcoin/minikit-js';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { KEY_REF_CODE, submitRefCode } from '../earn/hooks/useRefCode';
import { toastApp } from '../toast-message/sonner';

const WEEKEND_MILISECOND = 7 * 24 * 60 * 60 * 1000;
const DAY_MILISECOND = 24 * 60 * 60 * 1000;

export function useSignIn() {
  const { siweAuth } = useFeatureFlags();
  const [address, setLoading, setUserInfo] = useAuth(
    useShallow((state) => [state.address, state.setLoading, state.setUserInfo])
  );

  const location = useLocation();

  const signin = useCallback(async () => {
    if (address) {
      return address;
    }

    if (!MiniKit.isInstalled()) {
      throw new Error('MiniKit is not installed');
    }

    try {
      setLoading(true);
      const nonce = siweAuth
        ? await fetchNonce()
        : crypto.randomUUID().replace(/-/g, '');

      const { finalPayload } = await MiniKit.commandsAsync.walletAuth({
        nonce: nonce,
        requestId: '0', // Optional
        expirationTime: new Date(new Date().getTime() + WEEKEND_MILISECOND),
        notBefore: new Date(new Date().getTime() - DAY_MILISECOND),
        statement: '',
      });

      if (finalPayload.status !== 'success') {
        if (!finalPayload.details) {
          const msg = ErrorMessage(finalPayload.error_code);
          throw new Error(msg || 'Sign-in failed');
        }
        throw new Error(finalPayload.details);
      }

      if (siweAuth) {
        const tokens = await loginWithSiwe({
          address: finalPayload.address,
          message: finalPayload.message,
          signature: finalPayload.signature,
        });

        saveTokens(tokens);
      }

      const data = await MiniKit.getUserInfo(finalPayload.address);

      if (!data) {
        throw new Error('Userinfo not found');
      }

      let refCode = localStorage.getItem(KEY_AUTH);
      const pathname = location?.pathname || '';
      if (
        (pathname.length > 0 && pathname.startsWith('/ref')) ||
        pathname.startsWith('//ref')
      ) {
        const temp = pathname.split('/');
        refCode = temp[temp.length - 1];
      }

      await submitRefCode({
        signature: finalPayload.signature,
        address: finalPayload.address,
        message: finalPayload.message,
        refCode: refCode,
      });

      setUserInfo({
        address: data.walletAddress,
        profile_picture_url: data.profilePictureUrl ?? CONSTANT.DEFAULT_AVATAR,
        username: data.username ?? '',
      });

      localStorage.setItem(KEY_AUTH, data.walletAddress);

      return data.walletAddress;
    } catch (error: any) {
      console.error('Sign-in error:', error);
      if (siweAuth) {
        clearTokens();
      }
      toastApp.error(error);
    } finally {
      setLoading(false);
    }
  }, [address, location?.pathname, setLoading, setUserInfo, siweAuth]);

  useEffect(() => {
    let refCode = null;
    const pathname = location.pathname;

    console.debug('pathname', pathname);
    if (
      (pathname.length > 0 && pathname.startsWith('/ref')) ||
      pathname.startsWith('//ref')
    ) {
      const temp = pathname.split('/');
      refCode = temp[temp.length - 1];
      localStorage.setItem(KEY_REF_CODE, refCode);
    }
  }, [location.pathname]);

  return signin;
}
